<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportsAPI from 'dashboard/api/reports';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';
import { getLineChartFilters } from 'dashboard/helper/reportTimeRangeHelper';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const isLoading = ref(false);
const reportData = ref({});

const labels = computed(() => {
  const primarySeries = [
    reportData.value.incoming_messages_count,
    reportData.value.conversations_count,
    reportData.value.resolutions_count,
  ].find(series => series?.length);

  if (!primarySeries) return [];
  return primarySeries.map(item =>
    format(fromUnixTime(item.timestamp), 'dd MMM')
  );
});

const valueCollection = key => {
  return (reportData.value[key] || []).map(
    item => item.value || item.count || 0
  );
};

const dataset = (label, key, color) => ({
  label,
  data: valueCollection(key),
  borderColor: color,
  backgroundColor: color,
});

const chartCollection = computed(() => ({
  labels: labels.value,
  datasets: [
    dataset('Mensagens Recebidas', 'incoming_messages_count', '#1f93ff'),
    dataset('Conversas', 'conversations_count', '#14b8a6'),
    dataset('Resolvidas', 'resolutions_count', '#ef4444'),
  ],
}));

const fetchChartData = async () => {
  const {
    from,
    to,
    groupBy,
    businessHours,
    conversationDirection,
    comunicanteCampaign,
  } = getLineChartFilters(props.filters);
  if (!from || !to) return;

  const metrics = [
    'incoming_messages_count',
    'conversations_count',
    'resolutions_count',
  ];

  isLoading.value = true;
  try {
    const responses = await Promise.all(
      metrics.map(metric =>
        ReportsAPI.getReports({
          metric,
          from,
          to,
          groupBy: groupBy || 'day',
          businessHours,
          conversationDirection,
          comunicanteCampaign,
        })
      )
    );
    reportData.value = metrics.reduce((data, metric, index) => {
      data[metric] = responses[index].data || [];
      return data;
    }, {});
  } catch {
    reportData.value = {};
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.filters, fetchChartData, { deep: true });

onMounted(() => {
  fetchChartData();
});
</script>

<template>
  <ReportCard compact :is-loading="isLoading" :loading-message="loadingMessage">
    <div class="mb-4">
      <h3 class="mb-0 text-xl font-medium text-slate-900 dark:text-slate-50">
        {{ title }}
      </h3>
    </div>
    <div v-if="labels.length" class="w-full h-80">
      <ReportLineChart :collection="chartCollection" />
    </div>
    <div
      v-else
      class="flex items-center justify-center w-full h-80 text-sm text-slate-500 dark:text-slate-400"
    >
      {{ emptyMessage }}
    </div>
  </ReportCard>
</template>
